import React, { useEffect, useMemo, useState } from "react"
import { Row, Col, CardHeader, CardBody, Spinner, Alert, Button, Input } from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import Select from "react-select"
import { toast } from "react-toastify"
import {
  adminGetCompanyAddons,
  adminCancelCompanyAddon,
} from "../../api/requests/addon-requests"
import { fetchCompaniesNames } from "../../api/requests/account-requests"
import AddonCancelModal from "../modals/addon/AddonCancelModal"

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const NAVY = "#221C5D"
const GREEN = "#71C30B"
const GRAY = "#B0B7C3"
const RED_LT = "#E07A7A"

const formatPrice = (v) => {
  if (v == null) return "-"
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + " ₺"
}
const formatDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "—")

const statusColor = (status, effective) => {
  if (status === "ACTIVE") return effective ? GREEN : GRAY
  if (status === "PENDING") return "#F4A259"
  if (status === "CANCELLED") return RED_LT
  return GRAY
}

/**
 * Bir firmanın ek modülleri — süper yönetici görünümü.
 *
 * <h3>Neden "yürürlükte" ayrı bir sütun</h3>
 * Durumun ACTIVE olması hakkın <b>bugün</b> geçerli olduğunu göstermez: bitiş
 * tarihi geçmiş bir kayıt, gece çalışan zamanlayıcı onu EXPIRED yazana kadar
 * ACTIVE görünür. Hak çözümleyicisi tarihe de baktığı için panelde de iki
 * bilgiyi ayrı göstermek gerekir; aksi hâlde "aktif yazıyor ama çalışmıyor"
 * diye destek kaydı açılır.
 *
 * <h3>Neden katkılar modülden değil kayıttan okunuyor</h3>
 * Satın alma anında dondurulan değerler gösterilir. Aynı modülü almış iki
 * firmanın farklı sayılar görmesi <b>hata değildir</b>: modül tanımı arada
 * değişmiş olabilir ve satılmış hak korunur.
 */
const CompanyAddons = () => {
  const [companies, setCompanies] = useState([])
  const [companyId, setCompanyId] = useState(null)
  const [addons, setAddons] = useState([])
  const [loadingCompanies, setLoadingCompanies] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [cancelTarget, setCancelTarget] = useState(null)
  const [cancelOpen, setCancelOpen] = useState(false)

  document.title = "Firma Ek Modülleri | Tahsilat360"

  const styles = `
    .company-addons .table thead tr { background-color: #f9f9f9; }
    .company-addons .table th { border: 0; color: #6c757d; font-weight: 600; }
    .company-addons .table td { vertical-align: middle; }
    .company-addons .table-hover tbody tr:hover { background: #fcfcfd; }
    .company-addons .btn-rounded { border-radius: 999px; }
    .company-addons .action-btn { border-radius: 999px; padding: .35rem .6rem; }
    .company-addons .grant-chip {
      display: inline-block; background: #EBF0F7; color: ${NAVY};
      border-radius: 999px; padding: .1rem .5rem; font-size: .72rem;
      font-weight: 600; margin: .1rem .15rem .1rem 0;
    }
    @media (max-width: 576px) {
      .company-addons .table { font-size: 12px; }
      .company-addons .table td, .company-addons .table th { padding: .5rem; }
    }
  `

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetchCompaniesNames()
        if (!cancelled) setCompanies(res?.data || [])
      } catch (err) {
        if (!cancelled) setError(err.message || "Firmalar yüklenemedi")
      } finally {
        if (!cancelled) setLoadingCompanies(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const companyOptions = useMemo(
    () => companies.map((c) => ({ value: c.id, label: c.companyName || c.companyCode || "(isimsiz firma)" })),
    [companies]
  )

  const load = async (id) => {
    if (!id) return
    try {
      setError(null)
      setLoading(true)
      const res = await adminGetCompanyAddons(id)
      setAddons(res.data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { if (companyId) load(companyId) }, [companyId])

  const handleCancel = async (addon, reason) => {
    try {
      const res = await adminCancelCompanyAddon(addon.id, reason)
      toast.success(res?.message || "Modül iptal edildi")
      setCancelOpen(false)
      load(companyId)
    } catch (err) {
      toast.error(err.message || "Modül iptal edilemedi")
    }
  }

  const grants = (a) => {
    const parts = []
    if (a.grantedMaxAccounts) parts.push(`+${a.grantedMaxAccounts} cari`)
    if (a.grantedMaxUsers) parts.push(`+${a.grantedMaxUsers} kullanıcı`)
    if (a.grantedCollectionAmount) parts.push(`+${formatPrice(a.grantedCollectionAmount)} tahsilat`)
    if (a.grantedCollectionCount) parts.push(`+${a.grantedCollectionCount} adet`)
    if ((a.grantedFeatures || []).length > 0) parts.push(`${a.grantedFeatures.length} özellik`)
    return parts
  }

  return (
    <div className="page-content company-addons">
      <style>{styles}</style>
      <div className="container-fluid">
        {error && (
          <Alert color="danger" className="d-flex align-items-center justify-content-between">
            <span>{error}</span>
            <Button color="light" size="sm" onClick={() => load(companyId)}>Tekrar Dene</Button>
          </Alert>
        )}

        <Row>
          <Col xs={12}>
            <TableCard>
              <CardHeader className="bg-white border-0 rounded-top-4 pt-4 px-4">
                <h5 className="mb-1 fw-semibold" style={{ color: NAVY }}>Firma Ek Modülleri</h5>
                <div className="text-muted mb-3" style={{ fontSize: ".8rem" }}>
                  Bir firmanın satın aldığı ve kendisine tanımlanan modüllerin tamamı
                </div>
                <Row>
                  <Col md={5}>
                    <Select
                      options={companyOptions}
                      isLoading={loadingCompanies}
                      placeholder="Firma seçin..."
                      noOptionsMessage={() => "Firma bulunamadı"}
                      value={companyOptions.find((o) => o.value === companyId) || null}
                      onChange={(opt) => setCompanyId(opt ? opt.value : null)}
                    />
                  </Col>
                  <Col md={2} className="mt-2 mt-md-0">
                    <Button
                      className="btn-rounded w-100"
                      color="light"
                      onClick={() => load(companyId)}
                      disabled={!companyId || loading}
                    >
                      {loading ? <Spinner size="sm" /> : <i className="bx bx-refresh" />}
                    </Button>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="px-4 pb-4">
                {!companyId ? (
                  <div className="text-center py-5">
                    <i className="bx bx-buildings text-muted" style={{ fontSize: 56 }} />
                    <h6 className="mt-3 mb-1">Firma seçilmedi</h6>
                    <div className="text-muted" style={{ fontSize: ".85rem" }}>
                      Modüllerini görmek için yukarıdan bir firma seçin.
                    </div>
                  </div>
                ) : loading ? (
                  <div className="text-center py-5">
                    <Spinner size="lg" color="primary" />
                    <div className="mt-3 text-muted">Modüller yükleniyor...</div>
                  </div>
                ) : addons.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-extension text-muted" style={{ fontSize: 56 }} />
                    <h6 className="mt-3 mb-1">Bu firmada ek modül yok</h6>
                    <div className="text-muted" style={{ fontSize: ".85rem" }}>
                      Ek Modüller sayfasından “Firmaya tanımla” ile modül verebilirsiniz.
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table table-hover mb-0">
                      <Thead>
                        <Tr>
                          <Th>Modül</Th>
                          <Th>Durum</Th>
                          <Th>Dönem</Th>
                          <Th className="d-none d-md-table-cell">Katkılar</Th>
                          <Th className="d-none d-md-table-cell">Kaynak</Th>
                          <Th className="d-none d-md-table-cell">Ödenen</Th>
                          <Th className="text-end">İşlem</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {addons.map((a) => (
                          <Tr key={a.id}>
                            <Td>
                              <div className="fw-semibold">{a.moduleName}</div>
                              <div className="text-muted" style={{ fontSize: ".72rem" }}>{a.lifecycleLabel}</div>
                            </Td>
                            <Td>
                              <span
                                className="badge border-0"
                                style={{
                                  borderRadius: 999, fontWeight: 600, color: "#fff",
                                  background: statusColor(a.status, a.effective),
                                }}
                              >
                                {a.statusLabel || a.status}
                              </span>
                              {a.status === "ACTIVE" && !a.effective && (
                                <div className="text-muted" style={{ fontSize: ".7rem" }}>
                                  bugün yürürlükte değil
                                </div>
                              )}
                            </Td>
                            <Td style={{ fontSize: ".82rem" }}>
                              {formatDate(a.startDate)} → {a.endDate ? formatDate(a.endDate) : "Süresiz"}
                            </Td>
                            <Td className="d-none d-md-table-cell">
                              {grants(a).length === 0
                                ? <span className="text-muted" style={{ fontSize: ".78rem" }}>—</span>
                                : grants(a).map((g) => <span key={g} className="grant-chip">{g}</span>)}
                            </Td>
                            <Td className="d-none d-md-table-cell" style={{ fontSize: ".8rem" }}>
                              {a.sourceLabel || a.source}
                              {a.note && (
                                <div className="text-muted" style={{ fontSize: ".7rem" }}>{a.note}</div>
                              )}
                            </Td>
                            <Td className="d-none d-md-table-cell">{formatPrice(a.paidAmount)}</Td>
                            <Td className="text-end">
                              <Button
                                color="danger"
                                className="action-btn"
                                size="sm"
                                title="İptal et"
                                disabled={a.status === "CANCELLED"}
                                onClick={() => { setCancelTarget(a); setCancelOpen(true) }}
                              >
                                <i className="bx bx-x-circle" />
                              </Button>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </TableCard>
          </Col>
        </Row>
      </div>

      <AddonCancelModal
        isOpen={cancelOpen}
        toggle={() => setCancelOpen(false)}
        addon={cancelTarget}
        onConfirm={handleCancel}
      />
    </div>
  )
}

export default CompanyAddons
